/* eslint-disable max-lines */
import { procedureTypes } from 'lib/constants/constants';
import { inRn, mString, pString } from 'lib/helpers/utility';

import CurrencyCellEditor from 'components/shared/AGTable/components/CurrencyCellEditor';
import IncrementCellEditor from 'components/shared/AGTable/components/IncrementCellEditor';
import Dot from 'components/shared/Dot/Dot';
import { getValidationMessages } from 'components/validation/validate_currency_input';

import InvoiceDiscountCellRenderer from '../components/InvoiceDiscountCellRenderer';

// --------------------
// Constants
// --------------------
const EDITABLE_TYPES = ['primary', 'self_pay'];
const PROCEDURE_TYPES = ['primary', 'self_pay', 'unapplied'];
const EDITABLE_PRODUCT_TYPES = ['Product'];
const PRODUCT_TYPES = ['Product', 'Package'];

// --------------------
// Formatters
// --------------------
const defaultValueFormatter = (value) => mString(value || 0);

const productValueFormatter = ({ data, value }) =>
  PRODUCT_TYPES.includes(data?.type) ? mString(value) : '-';

const procedureValueFormatter = ({ data, value }) =>
  PROCEDURE_TYPES.includes(data?.type) ? defaultValueFormatter(value) : '-';

const cellDisabledClass = ({ data }) => (data?.type === 'unapplied' ? 'text-neutral-500' : '');

// --------------------
// Helpers
// --------------------
const renderDotValue = (value, isHighlighted) => (
  <div className="flex h-full w-full items-center gap-0.5">
    {isHighlighted && <Dot color="success" size="sm" />}
    <span className="px-2 text-sm leading-5">{mString(value)}</span>
  </div>
);

// --------------------
// Column Defs
// --------------------
export const EDIT_INVOICE_DEFAULT_COLUMN_DEFS = {
  width: 153
};

export const getEditInvoiceColumnDefs = ({ hasPrimaryInsurance = true } = {}) => {
  const allowedTypes = hasPrimaryInsurance
    ? PROCEDURE_TYPES
    : PROCEDURE_TYPES.filter((t) => t !== 'primary');
  const allowedProcedureTypes = procedureTypes.filter((t) => allowedTypes.includes(t.value));

  return [
    {
      field: 'name',
      headerName: 'Item',
      minWidth: 200
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 108,
      editable: ({ data }) => [...EDITABLE_TYPES, 'unapplied'].includes(data?.type),
      valueFormatter: ({ value }) => procedureTypes.find((type) => type.value === value)?.label,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: allowedProcedureTypes.map((type) => type.value),
        formatValue: (value) => procedureTypes.find((t) => t.value === value)?.label,
        searchDebounceDelay: 500,
        allowTyping: true,
        filterList: true,
        highlightMatch: true,
        valueListMaxHeight: 220
      }
    },
    {
      field: 'sales_count',
      headerName: 'Qty',
      editable: ({ data }) => EDITABLE_PRODUCT_TYPES.includes(data?.type),
      valueFormatter: ({ data, value }) => (PRODUCT_TYPES.includes(data?.type) ? value : '-'),
      cellClass: cellDisabledClass,
      cellEditor: IncrementCellEditor
    },
    {
      field: 'amount_cents',
      headerName: 'Unit Price',
      valueFormatter: productValueFormatter,
      cellClass: 'text-neutral-500'
    },
    {
      field: 'total_amount_cents',
      headerName: 'Price',
      valueFormatter: productValueFormatter,
      cellClass: 'text-neutral-500'
    },
    {
      field: 'charge',
      headerName: 'Ins. Charge',
      editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
      cellClass: cellDisabledClass,
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => {
        const { charge, units = 1, pt_balance, total_cost_cents } = params?.data || {};
        const value = (charge ?? pt_balance ?? total_cost_cents ?? 0) * units;
        return { value, initialValue: value };
      },
      valueFormatter: ({ data }) => {
        const { charge, units = 1, pt_balance, total_cost_cents } = data || {};
        const value = (charge ?? pt_balance ?? total_cost_cents ?? 0) * units;
        return procedureValueFormatter({ data, value });
      }
    },
    {
      field: 'pt_balance',
      headerName: 'Self pay Amt',
      editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
      cellClass: ({ data }) => {
        if (data?.type === 'self_pay') return 'text-success-600 px-0';
        if (data?.type === 'unapplied') return 'text-neutral-500';
        return '';
      },
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => {
        const { pt_balance, total_cost_cents, type } = params?.data || {};
        const value = pt_balance ?? total_cost_cents ?? 0;
        const isSelfPay = type === 'self_pay';
        return {
          dot: isSelfPay,
          value,
          initialValue: value,
          className: isSelfPay ? 'text-success-600' : 'px-2'
        };
      },
      cellRenderer: ({ data }) => {
        if (!PROCEDURE_TYPES.includes(data?.type)) return '-';
        return renderDotValue(
          data?.pt_balance ?? data?.total_cost_cents ?? 0,
          data?.type === 'self_pay'
        );
      }
    },
    {
      field: 'ins_payment',
      headerName: 'Allowed',
      editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
      cellClass: ({ data }) => {
        if (data?.type === 'primary') return 'text-success-600 px-0';
        if (data?.type === 'unapplied') return 'text-neutral-500';
        return '';
      },
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => {
        const { ins_payment, units = 1, type } = params?.data || {};
        const value = inRn(ins_payment) * units;
        const isPrimary = type === 'primary';
        return {
          dot: isPrimary,
          value,
          initialValue: value,
          className: isPrimary ? 'text-success-600' : 'px-2'
        };
      },
      cellRenderer: ({ data }) => {
        if (!PROCEDURE_TYPES.includes(data?.type)) return '-';

        return renderDotValue(
          (data?.ins_payment || 0) * (data?.units || 1),
          data?.type === 'primary'
        );
      }
    },
    {
      field: 'total_cost_cents',
      headerName: 'Est. Pt Portion',
      editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
      cellClass: cellDisabledClass,
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => {
        const { total_cost_cents, type, allowed, ins_payment, modified, pt_balance } =
          params?.data || {};
        let value = total_cost_cents ?? 0;
        if ((modified && pt_balance) || pt_balance > 0) value = pt_balance;
        return {
          value,
          initialValue: value,
          maxValue: type === 'primary' ? (allowed ?? ins_payment) : undefined,
          validationMessages: getValidationMessages('pt_portion')
        };
      },
      valueFormatter: ({ data }) => {
        let value = data?.total_cost_cents ?? 0;
        if ((data?.modified && data?.pt_balance) || data?.pt_balance > 0) {
          value = data.pt_balance;
        }
        return procedureValueFormatter({ data, value });
      }
    },
    {
      field: 'discount',
      headerName: 'Discount',
      cellRenderer: InvoiceDiscountCellRenderer,
      cellRendererParams: (params) => {
        const rowMaxValue = inRn(
          params?.data?.total_cost_cents ||
            params?.data?.total_amount_cents ||
            params?.data?.pt_balance
        );

        const invoiceBalance = inRn(params?.data?.invoice_balance, rowMaxValue);

        return {
          maxValue: Math.min(rowMaxValue, invoiceBalance),
          disabled: ![...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(params?.data?.type)
        };
      },
      cellClass: cellDisabledClass
    },
    {
      field: 'local_tax_rate',
      headerName: 'Local Tax',
      editable: ({ data }) => [...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(data?.type),
      cellClass: cellDisabledClass,
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => ({
        ...params,
        style: 'percent'
      }),
      valueFormatter: ({ data }) => pString(data?.local_tax_rate ?? 0)
    },
    {
      field: 'state_tax_rate',
      headerName: 'State Tax',
      editable: ({ data }) => [...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(data?.type),
      cellClass: cellDisabledClass,
      cellEditor: CurrencyCellEditor,
      cellEditorParams: (params) => ({
        ...params,
        style: 'percent'
      }),
      valueFormatter: ({ data }) => pString(data?.state_tax_rate ?? 0)
    }
  ];
};
